import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Shared scroll lock.
 *
 * `document.body` can only be locked once, but five components share
 * DialogComponent (cart drawer, media, predictive search, quick add, zoom
 * dialog) and one can open while another is already open — the cart drawer
 * auto-opens on `cart:add`. Tracking the scroll position per instance meant a
 * second dialog read `window.scrollY` with the body already `position: fixed`,
 * i.e. 0, and restored to 0 on close, throwing the page to the top.
 *
 * A plain refcount is NOT enough, and shipping one froze the page: every close
 * path that early-returns (`closeDialog` bails when the dialog is not open,
 * which happens if its element was replaced by a section re-render, or if
 * `showModal()` threw) left the count above zero. Because unlocking was gated
 * on reaching zero, the body stayed `position: fixed` forever and nothing
 * scrolled. The old code had the opposite bug but self-corrected, because it
 * cleared the styles unconditionally on any close.
 *
 * So holders are tracked as a set and reconciled against the DOM on every
 * release: a holder whose dialog is no longer open is dropped, and if no
 * `dialog[open]` remains anywhere the set is cleared outright. A stale holder
 * can never wedge the lock. Releasing is idempotent and restores instantly.
 */
/**
 * Scroll-lock strategy.
 *
 * 'fixed'    — current behaviour: position:fixed on the body. This collapses
 *              the document to viewport height, which is the root of the whole
 *              clamped-restore class of bugs.
 * 'overflow' — PROTOTYPE: overflow:hidden on html+body with
 *              overscroll-behavior:contain, and touch-action:pan-y on the
 *              drawer's scroll region. The document never collapses, so the
 *              scroll position is simply preserved and there is nothing to
 *              restore.
 *
 * Defaults to 'overflow'. `?scrolllock=fixed` is kept purely as an emergency
 * escape hatch — it switches back without a deploy if 'overflow' ever
 * misbehaves on a device we have not seen. Remembered in sessionStorage.
 */
const LOCK_MODE = (() => {
  try {
    const requested = new URLSearchParams(window.location.search).get('scrolllock');
    if (requested === 'overflow' || requested === 'fixed') {
      window.sessionStorage.setItem('sl-mode', requested);
    }
    return window.sessionStorage.getItem('sl-mode') || 'overflow';
  } catch (error) {
    return 'overflow';
  }
})();

if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.dataset.slMode = LOCK_MODE;

  if (LOCK_MODE === 'overflow') {
    // Keep the gesture inside the drawer: `overscroll-behavior: contain` stops
    // the scroll chaining to the page once the drawer's own region hits its
    // end, and `touch-action: pan-y` keeps that region vertically scrollable
    // while the page behind it is frozen by overflow:hidden.
    const style = document.createElement('style');
    style.id = 'sl-overflow-mode';
    style.textContent = [
      ":root[data-sl-mode='overflow'] dialog[open] { overscroll-behavior: contain; }",
      ":root[data-sl-mode='overflow'] dialog[open] .cart-drawer__content,",
      ":root[data-sl-mode='overflow'] dialog[open] [data-scroll-region] {",
      '  overscroll-behavior: contain;',
      '  touch-action: pan-y;',
      '  -webkit-overflow-scrolling: touch;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }
}

/** Whether we currently hold the lock. Authoritative — not sniffed from styles. */
let lockApplied = false;

let lockedScrollY = 0;

/**
 * Scroll position at the user's last interaction, and when it was taken.
 *
 * On the add-to-cart path `window.scrollY` already reads 0 by the time the
 * drawer opens — confirmed on device: `open raw=0 fixed=false`, with the body
 * not yet locked, so it is not a fixed-body artifact. The cart mutation
 * re-renders sections between the tap and the open, and the position is gone
 * before the dialog can save it. Anchoring to the position at the moment the
 * user actually tapped survives that, and is the position they expect to come
 * back to regardless of what zeroed it.
 */
let lastInteractionScrollY = 0;
let lastInteractionAt = 0;

if (typeof window !== 'undefined') {
  const recordInteraction = () => {
    lastInteractionScrollY = window.scrollY;
    lastInteractionAt = Date.now();
  };

  window.addEventListener('pointerdown', recordInteraction, { passive: true, capture: true });
  window.addEventListener('touchstart', recordInteraction, { passive: true, capture: true });
  window.addEventListener('keydown', recordInteraction, { passive: true, capture: true });
}

/**
 * The position to restore to. Prefers the live value, but falls back to the
 * last interaction when the page has been scrolled to 0 underneath us within
 * the last few seconds and the document is still long enough to scroll.
 * @param {number} scrollY - Value read at showDialog() time.
 * @returns {number}
 */
function resolveScrollY(scrollY) {
  if (scrollY > 0) return scrollY;
  if (lastInteractionScrollY <= 0) return scrollY;
  if (Date.now() - lastInteractionAt > 3000) return scrollY;
  if (document.documentElement.scrollHeight <= window.innerHeight) return scrollY;

  return lastInteractionScrollY;
}

/** @type {Set<DialogComponent>} */
const scrollLockHolders = new Set();

/**
 * Drops holders whose dialog is no longer open.
 * @returns {number} Holders still legitimately holding the lock.
 */
function reconcileScrollLock() {
  for (const holder of scrollLockHolders) {
    if (!holder.isConnected) {
      scrollLockHolders.delete(holder);
      continue;
    }
    if (!holder.refs?.dialog?.open) scrollLockHolders.delete(holder);
  }

  if (!document.querySelector('dialog[open]')) scrollLockHolders.clear();

  return scrollLockHolders.size;
}

/**
 * Locks the page, saving the scroll position.
 * @param {number} scrollY - Scroll position, read before any write.
 */
function applyScrollLock(scrollY) {
  lockedScrollY = scrollY;

  if (LOCK_MODE === 'overflow') {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  lockApplied = true;
}

let restoreFrame = 0;
/** @type {(() => void) | null} */
let restoreTeardown = null;

/** Stops any in-flight restore loop and detaches its listeners. */
function stopRestoreLoop() {
  if (restoreFrame) cancelAnimationFrame(restoreFrame);
  restoreFrame = 0;

  if (restoreTeardown) {
    restoreTeardown();
    restoreTeardown = null;
  }
}

/**
 * Re-asserts the scroll position every frame until it sticks.
 *
 * A single retry loses to any layout that takes more than one frame, and the
 * clamp has had more than one cause. Rather than identify every source, keep
 * re-applying for a short window: whatever clamps the scroll, the next frame
 * puts it back. A real user gesture wins immediately — if they start scrolling
 * we stop fighting them.
 *
 * @param {number} target - Position to restore.
 */
function startRestoreLoop(target) {
  stopRestoreLoop();

  const started = Date.now();
  let cancelled = false;
  let frames = 0;

  const onUserScroll = () => {
    cancelled = true;
  };

  window.addEventListener('touchmove', onUserScroll, { passive: true });
  window.addEventListener('wheel', onUserScroll, { passive: true });
  restoreTeardown = () => {
    window.removeEventListener('touchmove', onUserScroll);
    window.removeEventListener('wheel', onUserScroll);
  };

  const step = () => {
    restoreFrame = 0;

    if (cancelled) {
      stopRestoreLoop();
      return;
    }

    if (Math.abs(window.scrollY - target) <= 2) {
      stopRestoreLoop();
      return;
    }

    if (Date.now() - started > 400) {
      stopRestoreLoop();
      return;
    }

    frames += 1;
    window.scrollTo({ top: target, behavior: 'instant' });

    restoreFrame = requestAnimationFrame(step);
  };

  restoreFrame = requestAnimationFrame(step);
}

/**
 * Unlocks the body and restores the saved position. Safe to call twice.
 *
 * `position: fixed` on the body collapses the document to viewport height —
 * measured on device: `h=714 vh=714`, `h=674 vh=674`. If the scroll is
 * restored before layout has recomputed the full height, the browser CLAMPS it
 * to the collapsed maximum, i.e. roughly 0, and the page stays at the top.
 * That race is why the same close restored correctly one time and jumped to
 * the top the next, and why a later open then read a scroll position of 0.
 *
 * So: drop the styles, force a synchronous reflow so the document is tall
 * again, scroll, then re-assert on the next frame if the value did not take.
 */
function releaseScrollLock() {
  if (!lockApplied) return;
  lockApplied = false;

  const target = lockedScrollY;

  if (LOCK_MODE === 'overflow') {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    // The document never collapsed, so the position was never lost.
    return;
  }

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';

  // Force layout before scrolling, so the document is back to full height.
  void document.documentElement.offsetHeight;

  window.scrollTo({ top: target, behavior: 'instant' });

  startRestoreLoop(target);
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * True between showDialog() and the frame that actually opens the dialog.
   *
   * The add-to-cart path opens the cart drawer TWICE, synchronously:
   * CartAddEvent.eventName is ThemeEvents.cartUpdate, so the `auto-open`
   * listener fires on the same `cart:update` that pdp.js dispatches, and
   * pdp.js then calls drawer.open() as well. `dialog.open` is still false at
   * that point — the real open happens a frame later — so the guard above
   * does not catch it, and two frames raced: the second re-read the scroll
   * position with the body already fixed, and section morphing could swap the
   * dialog element between them. One pending open per component.
   */
  #openPending = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;
    if (this.#openPending) {
      return;
    }

    this.#openPending = true;

    // Read before any write. Only used when we are the first holder — a later
    // dialog would read 0 here, because the body is already fixed.
    const scrollY = window.scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#openPending = false;

      const alreadyLocked = reconcileScrollLock() > 0;

      if (!alreadyLocked) applyScrollLock(resolveScrollY(scrollY));

      try {
        dialog.showModal();
      } catch (error) {
        // Never leave the body locked for a dialog that failed to open.
        if (!alreadyLocked) {
          scrollLockHolders.clear();
          releaseScrollLock();
        }
        throw error;
      }

      // Registered only after showModal() succeeds.
      scrollLockHolders.add(this);
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      // The element may have been replaced by a section re-render while open.
      // Make sure this component is not left holding the lock.
      scrollLockHolders.delete(this);
      const remaining = reconcileScrollLock();
      if (remaining === 0) releaseScrollLock();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Close BEFORE releasing. `dialog.close()` returns focus to the element
    // that opened the dialog — for the cart drawer, the trigger in the header —
    // and the browser scrolls that element into view. Restoring first meant
    // that focus scroll ran afterwards and undid the restore. With the lock
    // still applied the body is fixed, so the focus scroll cannot move the
    // document; releasing then restores the saved position last.
    dialog.close();
    dialog.classList.remove('dialog-closing');

    scrollLockHolders.delete(this);
    const remaining = reconcileScrollLock();
    if (remaining === 0) releaseScrollLock();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
